/**
 * TourOverlay - the spotlight and step card for one running tour.
 *
 * Finds the step's control by data-tour, scrolls it into view, dims the rest
 * of the screen around it and places the card beside it (docked to the
 * bottom on a phone). If the control is not on screen within a few seconds -
 * hidden for this person, or the page is still loading - the card shows in
 * the middle instead, so a tour never gets stuck.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { TourDef } from './types';

const FIND_TIMEOUT_MS = 2500;
const PAD = 6;
const GAP = 12;
const PHONE_WIDTH = 560;

interface Rect { top: number; left: number; width: number; height: number }

function findTarget(id: string): HTMLElement | null {
  const nodes = document.querySelectorAll<HTMLElement>(`[data-tour="${CSS.escape(id)}"]`);
  for (const n of Array.from(nodes)) {
    const r = n.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return n;
  }
  return null;
}

export interface TourOverlayProps {
  tour: TourDef;
  step: number;
  navigate: (path: string) => void;
  onNext: () => void;
  onBack: () => void;
  onClose: () => void;
}

export function TourOverlay({ tour, step, navigate, onNext, onBack, onClose }: TourOverlayProps): JSX.Element {
  const s = tour.steps[step]!;
  const last = step === tour.steps.length - 1;
  const [rect, setRect] = useState<Rect | null>(null);
  const [searching, setSearching] = useState<boolean>(!!s.target);
  const [cardSize, setCardSize] = useState<{ w: number; h: number }>({ w: 360, h: 200 });
  const [viewport, setViewport] = useState({ w: window.innerWidth, h: window.innerHeight });
  const cardRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);

  // Move to the step's page - its own, or the last one an earlier step named, so Back lands
  // on the right screen - then find and follow its control.
  useEffect(() => {
    let route: string | undefined;
    for (let i = step; i >= 0 && !route; i--) route = tour.steps[i]?.route;
    if (route && window.location.pathname !== route) navigate(route);
    setRect(null);
    if (!s.target) { setSearching(false); return undefined; }
    setSearching(true);
    let raf = 0;
    let scrolled = false;
    const started = performance.now();
    const tick = (): void => {
      const el = findTarget(s.target!);
      if (el) {
        if (!scrolled) { el.scrollIntoView({ block: 'center', inline: 'nearest' }); scrolled = true; }
        const r = el.getBoundingClientRect();
        setRect((prev) => (prev && prev.top === r.top && prev.left === r.left && prev.width === r.width && prev.height === r.height)
          ? prev : { top: r.top, left: r.left, width: r.width, height: r.height });
        setSearching(false);
      } else if (performance.now() - started > FIND_TIMEOUT_MS) {
        setRect(null);
        setSearching(false);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour.id, step]);

  useEffect(() => {
    const onResize = (): void => setViewport({ w: window.innerWidth, h: window.innerHeight });
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); onNext(); }
      else if (e.key === 'ArrowLeft' && step > 0) { e.preventDefault(); onBack(); }
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('resize', onResize); window.removeEventListener('keydown', onKey); };
  }, [onClose, onNext, onBack, step]);

  useLayoutEffect(() => {
    const el = cardRef.current;
    if (el) setCardSize({ w: el.offsetWidth, h: el.offsetHeight });
    nextRef.current?.focus({ preventScroll: true });
  }, [step, rect === null, searching]);

  const phone = viewport.w < PHONE_WIDTH;
  let cardStyle: React.CSSProperties;
  if (phone) {
    cardStyle = { left: 12, right: 12, bottom: 12 };
  } else if (!rect) {
    cardStyle = { left: Math.max(12, (viewport.w - cardSize.w) / 2), top: Math.max(12, (viewport.h - cardSize.h) / 2) };
  } else {
    const below = viewport.h - (rect.top + rect.height + PAD);
    const above = rect.top - PAD;
    const right = viewport.w - (rect.left + rect.width + PAD);
    const want = s.placement && s.placement !== 'auto' ? s.placement
      : below >= cardSize.h + GAP ? 'bottom'
      : above >= cardSize.h + GAP ? 'top'
      : right >= cardSize.w + GAP ? 'right' : 'left';
    let top: number; let left: number;
    if (want === 'bottom' || want === 'top') {
      top = want === 'bottom' ? rect.top + rect.height + PAD + GAP : rect.top - PAD - GAP - cardSize.h;
      left = rect.left + rect.width / 2 - cardSize.w / 2;
    } else {
      left = want === 'right' ? rect.left + rect.width + PAD + GAP : rect.left - PAD - GAP - cardSize.w;
      top = rect.top + rect.height / 2 - cardSize.h / 2;
    }
    cardStyle = {
      top: Math.min(Math.max(12, top), viewport.h - cardSize.h - 12),
      left: Math.min(Math.max(12, left), viewport.w - cardSize.w - 12),
    };
  }

  const titleId = `tour-title-${tour.id}`;
  return (
    <div className="tour-root" data-support-exclude>
      <div className={`tour-blocker ${rect ? '' : 'is-dim'}`.trim()} aria-hidden />
      {rect && (
        <div className="tour-spot" aria-hidden style={{
          top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2,
        }} />
      )}
      <div ref={cardRef} className={`tour-card ${phone ? 'is-docked' : ''}`.trim()} style={cardStyle}
        role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="tour-card__meta">
          <span>{tour.title}</span>
          <span>Step {step + 1} of {tour.steps.length}</span>
        </div>
        <h2 id={titleId} className="tour-card__title">{s.title}</h2>
        <div className="tour-card__body">{searching ? <span className="tour-card__wait">One moment…</span> : s.body}</div>
        <div className="tour-card__dots" aria-hidden>
          {tour.steps.map((_, i) => <span key={i} className={i === step ? 'is-on' : i < step ? 'is-done' : ''} />)}
        </div>
        <div className="tour-card__actions">
          <button type="button" className="tour-btn tour-btn--link" onClick={onClose}>
            {last ? 'Close' : 'Skip the tour'}
          </button>
          <span className="tour-card__spacer" />
          {step > 0 && <button type="button" className="tour-btn" onClick={onBack}>Back</button>}
          <button ref={nextRef} type="button" className="tour-btn tour-btn--primary" onClick={onNext}>
            {last ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TourOverlay;
